import type { Granularity } from "@/types/filters";
import {
  getBucketLabel,
  getBucketRange,
  getWeeksInRange,
  type DateRange,
  type PeriodFamily,
} from "@/lib/periods";

export interface TrendPoint {
  bucketKey: string;
  label: string;
  seenRate: number;
  otherRate: number;
  blockoffRate: number;
  availRate: number;
  avgHoursPerDvmPerWeek: number;
  avgHoursPerDvmPerDay: number;
  daysPerDvmPerWeek: number;
  dvmsPerDay: number;
  totalHoursPerWeek: number;
}

export interface TrendSeries {
  current: TrendPoint[];
}

// Every raw sum a single trend bucket needs, whether produced by reducing
// rows client-side or by a SQL GROUP BY server-side — mirrors MetricTotals'
// role for the KPI cards. selectedKindMin is the sum of only the
// appointment-type-filtered kind columns; it's 0 (and unused) when no type
// filter is active.
export interface TrendBucketTotals {
  bucketKey: string;
  dvmDays: number;
  // Distinct (doctor, ISO week) pairs. The per-week denominator — see below.
  dvmWeeks: number;
  distinctDates: number;
  // Roster vs actual: totalScheduledMin drives the CAPACITY series,
  // totalWorkedMin the UTILIZATION rates. See MetricTotals in metrics.ts.
  totalScheduledMin: number;
  totalWorkedMin: number;
  totalBlockoffMin: number;
  allSeenMin: number;
  /** Sourced, not derived -- never negative. */
  totalAvailMin: number;
  selectedKindMin: number;
  // Revenue-tab measures. They ride on the shared bucket totals rather than on a
  // second bucketing pass because the bucketing rules -- the Thursday offset for
  // trailing months, the day-mask popcount, the calendar-month straddle -- are
  // the part that is easy to get wrong, and a second copy of them would be a
  // second chance to get them wrong differently.
  totalMedRev: number;
  totalCompleted: number;
}

// All of buildPoints' derived rates/ratios for one bucket, given pre-aggregated
// totals. Shared by the client-side (useTrendData) and server-side aggregation
// paths so they can't drift on arithmetic — only on how TrendBucketTotals
// itself gets built.
//
// `range` is the selected period, needed to clip edge buckets (below); `family`
// decides which month a trailing-period week belongs to (Thursday rule).
export function deriveTrendPoint(
  totals: TrendBucketTotals,
  granularity: Granularity,
  family: PeriodFamily,
  range: DateRange,
  typeFilterActive: boolean
): TrendPoint {
  const { bucketKey, dvmDays, dvmWeeks, distinctDates } = totals;
  const sched = totals.totalScheduledMin;
  const worked = totals.totalWorkedMin;
  const blockoff = totals.totalBlockoffMin;
  const allSeen = totals.allSeenMin;
  const avail = totals.totalAvailMin;

  const seen = typeFilterActive ? totals.selectedKindMin : allSeen;
  const other = typeFilterActive ? Math.max(0, allSeen - seen) : 0;
  const dvmHours = sched / 60;

  // Weeks this bucket spans, from its own calendar boundaries — not from how
  // many days DVMs happened to work (see useTrendData.ts for why: clinics are
  // staffed all 7 days, so a distinct-scheduled-days heuristic understates).
  //
  // Clipped to the selected period, because the first and last month bucket of
  // any window are cut in half by its edges. A T52W ending mid-August holds one
  // week of that August while the month itself has four; dividing by four would
  // report a quarter of the truth and put a cliff at the end of the chart.
  const bucketRange = getBucketRange(bucketKey, granularity, family);
  const weeksInBucket = getWeeksInRange(
    bucketRange.startDate > range.startDate ? bucketRange.startDate : range.startDate,
    bucketRange.endDate < range.endDate ? bucketRange.endDate : range.endDate
  );

  // Divided by DVM-weeks — distinct (doctor, week) pairs — not weeks x distinct
  // doctors, which assumes every doctor worked every week and so dilutes a
  // month bucket whenever the roster moves. Identical at week granularity.
  //
  // Clipped at a partial WEEK bucket. A calendar period's first or last week can
  // be a two-day stub, and a doctor who worked both days still contributes one
  // whole DVM-week -- so their hours would be divided by 1 when only 2/7 of a week
  // elapsed, reading ~3.5x low. weeksInBucket is exactly that fraction for a week
  // bucket (1.0 when whole, 0.2857 for two days), so scaling by it is exact.
  //
  // Week granularity only. A month bucket holds several weeks of which at most one
  // is clipped, so one scale factor would be an approximation -- that bucket is
  // unchanged, and section 4.4.3 already calls a calendar period's last point
  // provisional.
  const effectiveDvmWeeks = granularity === "week" ? dvmWeeks * weeksInBucket : dvmWeeks;

  const avgHoursPerDvmPerWeek = effectiveDvmWeeks > 0 ? dvmHours / effectiveDvmWeeks : 0;
  const avgHoursPerDvmPerDay = dvmDays > 0 ? dvmHours / dvmDays : 0;
  const daysPerDvmPerWeek = effectiveDvmWeeks > 0 ? dvmDays / effectiveDvmWeeks : 0;
  const dvmsPerDay = distinctDates > 0 ? dvmDays / distinctDates : 0;
  const totalHoursPerWeek = weeksInBucket > 0 ? dvmHours / weeksInBucket : 0;

  return {
    bucketKey,
    label: getBucketLabel(bucketKey, granularity),
    // Over WORKED minutes, matching deriveMetrics: the three buckets sum to
    // total_worked_minutes exactly, so this is the only denominator that makes
    // the stacked chart reach 100% without available time acting as a plug.
    seenRate: worked > 0 ? seen / worked : 0,
    otherRate: worked > 0 ? other / worked : 0,
    blockoffRate: worked > 0 ? blockoff / worked : 0,
    availRate: worked > 0 ? avail / worked : 0,
    avgHoursPerDvmPerWeek,
    avgHoursPerDvmPerDay,
    daysPerDvmPerWeek,
    dvmsPerDay,
    totalHoursPerWeek,
  };
}
